import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar, Clock, CheckCircle, Loader2, ChevronLeft, ChevronRight, Info, CalendarDays, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Analog Clock Component
const AnalogClock = ({ hour, minute, onHourChange, onMinuteChange, label }) => {
  const [mode, setMode] = useState('hour'); // 'hour' or 'minute'
  
  const hourAngle = (hour % 12) * 30 + (minute / 60) * 30;
  const minuteAngle = minute * 6;
  
  const handleClockClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const x = e.clientX - rect.left - centerX;
    const y = e.clientY - rect.top - centerY;
    
    let angle = Math.atan2(x, -y) * (180 / Math.PI);
    if (angle < 0) angle += 360;
    
    if (mode === 'hour') {
      let newHour = Math.round(angle / 30);
      if (newHour === 0) newHour = 12;
      // Convert to 24-hour format based on AM/PM
      if (hour >= 12 && newHour !== 12) newHour += 12;
      if (hour < 12 && newHour === 12) newHour = 0;
      onHourChange(newHour);
      setMode('minute');
    } else {
      const newMinute = Math.round(angle / 6) % 60;
      onMinuteChange(Math.round(newMinute / 15) * 15); // Snap to 15-min intervals
    }
  };
  
  return (
    <div className="flex flex-col items-center">
      <p className="text-sm text-slate-600 mb-2 font-medium">{label}</p>
      <div 
        className="relative w-36 h-36 rounded-full bg-gradient-to-br from-slate-50 to-slate-100 border-4 border-slate-200 shadow-inner cursor-pointer"
        onClick={handleClockClick}
      >
        {/* Clock face numbers */}
        {[12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((num, i) => {
          const angle = (i * 30 - 90) * (Math.PI / 180);
          const x = 50 + 38 * Math.cos(angle);
          const y = 50 + 38 * Math.sin(angle);
          return (
            <span
              key={num}
              className="absolute text-xs font-semibold text-slate-600"
              style={{
                left: `${x}%`,
                top: `${y}%`,
                transform: 'translate(-50%, -50%)'
              }}
            >
              {num}
            </span>
          );
        })}
        
        {/* Hour hand */}
        <div
          className="absolute left-1/2 bottom-1/2 w-1.5 h-10 bg-slate-700 rounded-full origin-bottom transition-transform duration-200"
          style={{ transform: `translateX(-50%) rotate(${hourAngle}deg)` }}
        />
        
        {/* Minute hand */}
        <div
          className="absolute left-1/2 bottom-1/2 w-1 h-14 bg-blue-500 rounded-full origin-bottom transition-transform duration-200"
          style={{ transform: `translateX(-50%) rotate(${minuteAngle}deg)` }}
        />
        
        {/* Center dot */}
        <div className="absolute left-1/2 top-1/2 w-3 h-3 bg-blue-600 rounded-full transform -translate-x-1/2 -translate-y-1/2 border-2 border-white" />
        
        {/* Mode indicator */}
        <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2">
          <span className={`text-xs font-medium ${mode === 'hour' ? 'text-blue-600' : 'text-slate-400'}`}>
            {mode === 'hour' ? 'Set hour' : 'Set minutes'}
          </span>
        </div>
      </div>
      
      {/* Digital display */}
      <div className="mt-3 flex items-center gap-1 bg-slate-800 rounded-lg px-4 py-2">
        <button
          onClick={() => setMode('hour')}
          className={`text-2xl font-mono ${mode === 'hour' ? 'text-blue-400' : 'text-white'}`}
        >
          {String(hour).padStart(2, '0')}
        </button>
        <span className="text-2xl font-mono text-white">:</span>
        <button
          onClick={() => setMode('minute')}
          className={`text-2xl font-mono ${mode === 'minute' ? 'text-blue-400' : 'text-white'}`}
        >
          {String(minute).padStart(2, '0')}
        </button>
      </div>
      
      {/* Quick time buttons */}
      <div className="flex gap-1 mt-2">
        {mode === 'hour' ? (
          <>
            <button onClick={() => { onHourChange(9); setMode('minute'); }} className="text-xs px-2 py-1 rounded bg-slate-100 hover:bg-slate-200">9</button>
            <button onClick={() => { onHourChange(10); setMode('minute'); }} className="text-xs px-2 py-1 rounded bg-slate-100 hover:bg-slate-200">10</button>
            <button onClick={() => { onHourChange(14); setMode('minute'); }} className="text-xs px-2 py-1 rounded bg-slate-100 hover:bg-slate-200">14</button>
            <button onClick={() => { onHourChange(15); setMode('minute'); }} className="text-xs px-2 py-1 rounded bg-slate-100 hover:bg-slate-200">15</button>
          </>
        ) : (
          <>
            <button onClick={() => onMinuteChange(0)} className="text-xs px-2 py-1 rounded bg-slate-100 hover:bg-slate-200">:00</button>
            <button onClick={() => onMinuteChange(15)} className="text-xs px-2 py-1 rounded bg-slate-100 hover:bg-slate-200">:15</button>
            <button onClick={() => onMinuteChange(30)} className="text-xs px-2 py-1 rounded bg-slate-100 hover:bg-slate-200">:30</button>
            <button onClick={() => onMinuteChange(45)} className="text-xs px-2 py-1 rounded bg-slate-100 hover:bg-slate-200">:45</button>
          </>
        )}
      </div>
    </div>
  );
};

export default function InterviewSchedulePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedDates, setSelectedDates] = useState([]);
  const [startHour, setStartHour] = useState(10);
  const [startMinute, setStartMinute] = useState(0);
  const [endHour, setEndHour] = useState(18);
  const [endMinute, setEndMinute] = useState(0);
  const [duration, setDuration] = useState('45');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [success, setSuccess] = useState(false);
  const [candidateLink, setCandidateLink] = useState(null);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }
    
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  const toggleDate = (date) => {
    if (!date || date < new Date().setHours(0, 0, 0, 0)) return;
    
    const dateStr = formatDate(date);
    if (selectedDates.includes(dateStr)) {
      setSelectedDates(selectedDates.filter(d => d !== dateStr));
    } else {
      setSelectedDates([...selectedDates, dateStr]);
    }
  };

  const formatTime = (hour, minute) => {
    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  };

  const handleSubmit = async () => {
    if (selectedDates.length === 0) {
      toast.error('Please select at least one available date');
      return;
    }

    const timeStart = formatTime(startHour, startMinute);
    const timeEnd = formatTime(endHour, endMinute);

    if (timeStart >= timeEnd) {
      toast.error('End time must be after start time');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      selectedDates.forEach(d => formData.append('available_dates', d));
      formData.append('time_start', timeStart);
      formData.append('time_end', timeEnd);
      formData.append('duration', duration);

      const res = await fetch(`${API_URL}/api/hr-pipeline/schedule/${token}`, {
        method: 'POST',
        body: formData
      });

      if (!res.ok) throw new Error('Failed to create schedule');
      
      const result = await res.json();
      setSuccess(true);
      setCandidateLink(result.candidate_link);
      toast.success('Interview slots created! Candidate will be notified.');
    } catch (err) {
      toast.error('Failed to create interview slots');
    } finally {
      setLoading(false);
    }
  };

  const days = getDaysInMonth(currentMonth);
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-xl border-0">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800 mb-3">Interview Scheduled! 🎉</h1>
            <p className="text-slate-600 mb-6">
              The candidate will receive an email with {selectedDates.length} available day(s) to choose from.
            </p>
            <div className="bg-slate-50 rounded-xl p-4 mb-6">
              <p className="text-sm text-slate-500 mb-2">Available slots sent:</p>
              <p className="font-semibold text-slate-700">
                {selectedDates.length} day(s) × {formatTime(startHour, startMinute)} - {formatTime(endHour, endMinute)}
              </p>
              <p className="text-sm text-slate-500 mt-1">{duration} minutes per slot</p>
            </div>
            <p className="text-sm text-slate-500">
              You'll be notified via email & WhatsApp when the candidate confirms their preferred slot.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Calendar className="w-6 h-6 text-blue-600" />
            Schedule Interview
          </h1>
          <p className="text-sm text-slate-500 mt-1">Set your availability for the candidate</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        
        {/* Info Banner */}
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl p-5 text-white shadow-lg">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold mb-1">Quick Tip</h3>
              <p className="text-sm text-blue-100">
                Select <strong>multiple days</strong> to give the candidate more flexibility. 
                They'll pick one slot that works best for them.
              </p>
            </div>
          </div>
        </div>

        {/* Step 1: Select Dates */}
        <Card className="shadow-lg border-0 overflow-hidden">
          <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-5 py-3">
            <h2 className="text-white font-semibold flex items-center gap-2">
              <span className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-sm">1</span>
              Select Available Dates
            </h2>
          </div>
          <CardContent className="p-5">
            <p className="text-sm text-slate-500 mb-4 flex items-center gap-2">
              <CalendarDays className="w-4 h-4" />
              Click on dates when you're free for an interview
            </p>

            {/* Calendar Navigation */}
            <div className="flex items-center justify-between mb-4 bg-slate-50 rounded-xl p-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <span className="font-bold text-slate-800 text-lg">
                {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-2 mb-4">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-xs font-semibold text-slate-400 py-2">
                  {day}
                </div>
              ))}
              {days.map((date, i) => {
                if (!date) return <div key={i} />;
                
                const dateStr = formatDate(date);
                const isSelected = selectedDates.includes(dateStr);
                const isPast = date < new Date().setHours(0, 0, 0, 0);
                const isToday = formatDate(date) === formatDate(new Date());
                
                return (
                  <button
                    key={i}
                    onClick={() => toggleDate(date)}
                    disabled={isPast}
                    className={`
                      aspect-square rounded-xl text-sm font-semibold transition-all duration-200
                      ${isPast ? 'text-slate-200 cursor-not-allowed' : 'hover:scale-105 cursor-pointer'}
                      ${isSelected ? 'bg-blue-600 text-white shadow-lg shadow-blue-200' : 'bg-slate-50 hover:bg-slate-100'}
                      ${isToday && !isSelected ? 'ring-2 ring-blue-400 ring-offset-2' : ''}
                    `}
                  >
                    {date.getDate()}
                  </button>
                );
              })}
            </div>

            {/* Selected Dates Summary */}
            {selectedDates.length > 0 ? (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-100">
                <p className="text-sm text-green-800 font-bold mb-2 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  {selectedDates.length} day(s) selected
                </p>
                <div className="flex flex-wrap gap-2">
                  {selectedDates.sort().map(date => (
                    <span 
                      key={date} 
                      className="text-xs bg-white px-3 py-1.5 rounded-full text-green-700 font-medium shadow-sm border border-green-100"
                    >
                      {new Date(date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                <p className="text-sm text-amber-700 flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  Select at least one date to continue
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Step 2: Set Time Range with Analog Clocks */}
        <Card className="shadow-lg border-0 overflow-hidden">
          <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-5 py-3">
            <h2 className="text-white font-semibold flex items-center gap-2">
              <span className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-sm">2</span>
              Set Time Range
            </h2>
          </div>
          <CardContent className="p-5">
            <p className="text-sm text-slate-500 mb-6 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Click on the clock to set hours, then minutes
            </p>

            <div className="flex flex-wrap justify-center gap-8 mb-6">
              <AnalogClock 
                hour={startHour}
                minute={startMinute}
                onHourChange={setStartHour}
                onMinuteChange={setStartMinute}
                label="Available From"
              />
              <div className="flex items-center">
                <div className="text-2xl text-slate-300">→</div>
              </div>
              <AnalogClock 
                hour={endHour}
                minute={endMinute}
                onHourChange={setEndHour}
                onMinuteChange={setEndMinute}
                label="Available Until"
              />
            </div>

            {/* Time Summary */}
            <div className="bg-slate-50 rounded-xl p-4 text-center">
              <p className="text-sm text-slate-500 mb-1">Your availability window:</p>
              <p className="text-xl font-bold text-slate-800">
                {formatTime(startHour, startMinute)} - {formatTime(endHour, endMinute)}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Step 3: Interview Duration */}
        <Card className="shadow-lg border-0 overflow-hidden">
          <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-5 py-3">
            <h2 className="text-white font-semibold flex items-center gap-2">
              <span className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-sm">3</span>
              Interview Duration
            </h2>
          </div>
          <CardContent className="p-5">
            <div className="grid grid-cols-4 gap-3">
              {[
                { value: '30', label: '30 min', desc: 'Quick chat' },
                { value: '45', label: '45 min', desc: 'Standard' },
                { value: '60', label: '60 min', desc: 'In-depth' },
                { value: '90', label: '90 min', desc: 'Technical' }
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setDuration(opt.value)}
                  className={`
                    p-4 rounded-xl text-center transition-all duration-200
                    ${duration === opt.value 
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-200' 
                      : 'bg-slate-50 hover:bg-slate-100 text-slate-700'
                    }
                  `}
                >
                  <p className="font-bold text-lg">{opt.label}</p>
                  <p className={`text-xs mt-1 ${duration === opt.value ? 'text-blue-100' : 'text-slate-400'}`}>
                    {opt.desc}
                  </p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <Button 
          onClick={handleSubmit} 
          disabled={loading || selectedDates.length === 0}
          className="w-full h-14 text-lg font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-xl shadow-lg"
          size="lg"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
          ) : (
            <Calendar className="w-5 h-5 mr-2" />
          )}
          Send {selectedDates.length > 0 ? `${selectedDates.length} Day(s)` : ''} to Candidate
        </Button>

        {/* Footer Note */}
        <p className="text-center text-xs text-slate-400 pb-4">
          The candidate will receive an email with time slots to choose from.
          You'll be notified once they confirm.
        </p>
      </div>
    </div>
  );
}
